import { isGenericHttpFailureMessage, isHttpClientError } from "../../../lib/http/errors";
import type {
  GeoBusinessCandidate,
  GeoBusinessScenario,
  GeoBusinessVerifyResult,
  GeoOperationStatus,
} from "../../../shared/business-geo";
import {
  getCityOptions,
  getDistrictOptions,
  getProvinceOptions,
} from "../../../shared/region";
import type {
  ResourcePointGeoStatus,
  ResourcePointHeritageDescription,
  ResourcePointListFilters,
  ResourcePointListParams,
  ResourcePointMediaItem,
  ResourcePointMediaMutation,
  ResourcePointMediaScene,
  ResourcePointSkuItem,
  ResourcePointSkuMutation,
  ResourcePointSpuItem,
  ResourcePointSpuMutation,
} from "../types";
import {
  resourcePointDefaultListParams,
  resourcePointMediaSceneOptions,
  resourcePointSettlementUnits,
  resourcePointSortFields,
  resourcePointStatuses,
  resourcePointSubTypes,
  resourcePointTicketResourceTypes,
  resourcePointTravelerRoles,
  scopeLockedListFilters,
  type ResourcePointListScope,
} from "../config";

export type ResourcePointSpuMutationField =
  | "pointType"
  | "pointSubType"
  | "pointName"
  | "pointShortName"
  | "headImageUrl"
  | "country"
  | "province"
  | "city"
  | "district"
  | "address"
  | "lng"
  | "lat"
  | "visitMode"
  | "teachingTags"
  | "region"
  | "isDisabled"
  | "status"
  | "remark";

export type ResourcePointSpuMutationErrors = Partial<
  Record<ResourcePointSpuMutationField, string>
>;

export type ResourcePointSkuMutationField =
  | "skuName"
  | "ticketResourceType"
  | "travelerRole"
  | "settlementUnit"
  | "reservationRequired"
  | "minGroupSize"
  | "maxGroupSize"
  | "groupSize"
  | "isDisabled"
  | "status"
  | "remark";

export type ResourcePointSkuMutationErrors = Partial<
  Record<ResourcePointSkuMutationField, string>
>;

export type ResourcePointMediaMutationField =
  | "mediaScene"
  | "mediaName"
  | "fileUrl"
  | "contentText"
  | "sortNo"
  | "remark";

export type ResourcePointMediaMutationErrors = Partial<
  Record<ResourcePointMediaMutationField, string>
>;

const mediaSceneValues = new Set(
  resourcePointMediaSceneOptions.map((item) => item.value),
);

export const emptyHeritageDescription: ResourcePointHeritageDescription = {
  brief: "",
  visitFlow: "",
  experienceFlow: "",
  priceOther: "",
};

/** 将接口日期时间转换为页面展示文本，不做时区换算。 */
export function formatResourcePointDateTime(value?: string): string {
  return value ? value.replace("T", " ") : "-";
}

/** 从 SKU 列表项构造更新接口需要的完整可编辑载荷。 */
export function toResourcePointSkuMutation(
  sku: ResourcePointSkuItem,
  overrides: Partial<ResourcePointSkuMutation> = {},
): ResourcePointSkuMutation {
  return {
    skuName: sku.skuName,
    ticketResourceType: sku.ticketResourceType,
    travelerRole: sku.travelerRole,
    settlementUnit: sku.settlementUnit,
    reservationRequired: sku.reservationRequired,
    reservationInfoMarkdown: sku.reservationInfoMarkdown ?? "",
    minGroupSize: sku.minGroupSize,
    maxGroupSize: sku.maxGroupSize,
    isDisabled: sku.isDisabled,
    status: sku.status === "inactive" ? "inactive" : "active",
    remark: sku.remark,
    ...overrides,
  };
}

/** 从考察点列表项构造更新接口需要的完整可编辑载荷。 */
export function toResourcePointSpuMutation(
  spu: ResourcePointSpuItem,
): ResourcePointSpuMutation {
  return {
    pointType: spu.pointType,
    pointSubType: spu.pointSubType,
    pointName: spu.pointName,
    pointShortName: spu.pointShortName ?? "",
    headImageUrl: spu.headImageUrl ?? "",
    country: spu.country,
    province: spu.province ?? "",
    city: spu.city ?? "",
    district: spu.district ?? "",
    address: spu.address ?? "",
    lng: spu.lng,
    lat: spu.lat,
    visitMode: spu.visitMode ?? "",
    teachingTags: spu.teachingTags ?? "",
    isDisabled: spu.isDisabled,
    status: spu.status === "inactive" ? "inactive" : "active",
    remark: spu.remark,
  };
}

/** 已确认 GEO 结论回填省市区、标准地址和坐标。 */
export function applyResourcePointConfirmedGeo(
  current: ResourcePointSpuMutation,
  result: GeoBusinessVerifyResult,
): ResourcePointSpuMutation {
  return {
    ...current,
    province: result.province || current.province,
    city: result.city || current.city,
    district: result.district || current.district,
    address: result.amapStandardAddress || current.address,
    lng: result.longitude,
    lat: result.latitude,
  };
}

/** 人工采用或草稿回填高德候选后写入省市区、地址和坐标。 */
export function applyResourcePointGeoCandidate(
  current: ResourcePointSpuMutation,
  candidate: GeoBusinessCandidate,
): ResourcePointSpuMutation {
  return {
    ...current,
    province: candidate.province || current.province,
    city: candidate.city || current.city,
    district: candidate.district || current.district,
    address: candidate.address || candidate.name || current.address,
    lng: candidate.longitude,
    lat: candidate.latitude,
  };
}

/** 按当前 GEO 面板状态把已确认结论或待修正草稿候选写入表单。 */
export function fillResourcePointMutationFromGeo(
  current: ResourcePointSpuMutation,
  geoStatus: GeoOperationStatus,
  result?: GeoBusinessVerifyResult,
): ResourcePointSpuMutation {
  if (!result) return current;
  if (geoStatus === "confirmed") {
    return applyResourcePointConfirmedGeo(current, result);
  }
  if (geoStatus !== "needs_correction") return current;
  const draftCandidate = result.candidates[0];
  if (draftCandidate) {
    return applyResourcePointGeoCandidate(current, draftCandidate);
  }
  if (result.amapStandardAddress || result.district) {
    return applyResourcePointConfirmedGeo(current, result);
  }
  return current;
}

/** 校验考察点 SPU 新增或编辑值，返回可直接绑定表单的错误集合。 */
export function validateSpuMutation(
  payload: ResourcePointSpuMutation,
  preservedPointSubType?: string,
  entityLabel = "考察点",
  requirePointSubType = false,
): ResourcePointSpuMutationErrors {
  const errors: ResourcePointSpuMutationErrors = {};
  const pointType = payload.pointType.trim();
  const pointName = payload.pointName.trim();
  const country = payload.country.trim();
  const nameLabel = `${entityLabel}名称`;
  const shortNameLabel = `${entityLabel}简称`;

  if (!pointType) {
    errors.pointType = "考察点类型为必填";
  } else if (codePointLength(pointType) > 50) {
    errors.pointType = "考察点类型不能超过 50 个字符";
  }
  if (requirePointSubType && pointType === "考察点" && !payload.pointSubType?.trim()) {
    errors.pointSubType = "考察点二级类型为必填";
  }
  addOptionalLengthError(
    errors,
    "pointSubType",
    payload.pointSubType,
    100,
    "考察点二级类型不能超过 100 个字符",
  );
  if (
    payload.pointSubType
    && !errors.pointSubType
    && !resourcePointSubTypes.includes(payload.pointSubType as never)
    && payload.pointSubType !== preservedPointSubType
  ) {
    errors.pointSubType = "考察点二级类型不正确";
  }
  if (!pointName) {
    errors.pointName = `${nameLabel}为必填`;
  } else if (codePointLength(pointName) > 300) {
    errors.pointName = `${nameLabel}不能超过 300 个字符`;
  }
  addOptionalLengthError(
    errors,
    "pointShortName",
    payload.pointShortName,
    150,
    `${shortNameLabel}不能超过 150 个字符`,
  );
  addOptionalLengthError(
    errors,
    "headImageUrl",
    payload.headImageUrl,
    1000,
    "头图地址不能超过 1000 个字符",
  );
  if (!country) {
    errors.country = "国家为必填";
  } else if (codePointLength(country) > 100) {
    errors.country = "国家不能超过 100 个字符";
  }
  addOptionalLengthError(
    errors,
    "province",
    payload.province,
    100,
    "省份不能超过 100 个字符",
  );
  addOptionalLengthError(errors, "city", payload.city, 100, "城市不能超过 100 个字符");
  addOptionalLengthError(
    errors,
    "district",
    payload.district,
    100,
    "区县不能超过 100 个字符",
  );
  addOptionalLengthError(
    errors,
    "address",
    payload.address,
    500,
    "详细地址不能超过 500 个字符",
  );
  addCoordinateError(errors, "lng", payload.lng, -180, 180, "经度必须在 -180 至 180 之间");
  addCoordinateError(errors, "lat", payload.lat, -90, 90, "纬度必须在 -90 至 90 之间");
  addOptionalLengthError(
    errors,
    "visitMode",
    payload.visitMode,
    100,
    "参观方式不能超过 100 个字符",
  );
  addOptionalLengthError(
    errors,
    "teachingTags",
    payload.teachingTags,
    1000,
    "教学标签不能超过 1000 个字符",
  );
  if (country === "中国" && !isValidChinaRegion(payload)) {
    errors.region = "中国省、市、区组合不正确";
  }
  if (typeof payload.isDisabled !== "boolean") {
    errors.isDisabled = "停用状态不正确";
  }
  if (!resourcePointStatuses.includes(payload.status)) {
    errors.status = "考察点状态不正确";
  }
  if (codePointLength(payload.remark) > 1000) {
    errors.remark = "备注不能超过 1000 个字符";
  }
  return errors;
}

/** 校验考察点 SKU 新增或编辑值，返回可直接绑定表单的错误集合。 */
export function validateSkuMutation(
  payload: ResourcePointSkuMutation,
  preservedValues?: Pick<
    ResourcePointSkuMutation,
    "ticketResourceType" | "travelerRole" | "settlementUnit"
  >,
): ResourcePointSkuMutationErrors {
  const errors: ResourcePointSkuMutationErrors = {};
  const skuName = payload.skuName.trim();

  if (!skuName) {
    errors.skuName = "SKU 名称为必填";
  } else if (codePointLength(skuName) > 300) {
    errors.skuName = "SKU 名称不能超过 300 个字符";
  }
  addRequiredSkuTextError(
    errors,
    "ticketResourceType",
    payload.ticketResourceType,
    100,
    "票务资源类型为必填",
    "票务资源类型不能超过 100 个字符",
  );
  if (
    payload.ticketResourceType.trim()
    && !errors.ticketResourceType
    && !resourcePointTicketResourceTypes.includes(payload.ticketResourceType as never)
    && payload.ticketResourceType !== preservedValues?.ticketResourceType
  ) {
    errors.ticketResourceType = "票务资源类型不正确";
  }
  addRequiredSkuTextError(
    errors,
    "travelerRole",
    payload.travelerRole,
    100,
    "旅客角色为必填",
    "旅客角色不能超过 100 个字符",
  );
  if (
    payload.travelerRole.trim()
    && !errors.travelerRole
    && !resourcePointTravelerRoles.includes(payload.travelerRole as never)
    && payload.travelerRole !== preservedValues?.travelerRole
  ) {
    errors.travelerRole = "旅客角色不正确";
  }
  addRequiredSkuTextError(
    errors,
    "settlementUnit",
    payload.settlementUnit,
    30,
    "结算单位为必填",
    "结算单位不能超过 30 个字符",
  );
  if (
    payload.settlementUnit.trim()
    && !errors.settlementUnit
    && !resourcePointSettlementUnits.includes(payload.settlementUnit as never)
    && payload.settlementUnit !== preservedValues?.settlementUnit
  ) {
    errors.settlementUnit = "结算单位不正确";
  }
  if (typeof payload.reservationRequired !== "boolean") {
    errors.reservationRequired = "预约要求状态不正确";
  }
  const minValid = addGroupSizeError(errors, "minGroupSize", payload.minGroupSize);
  const maxValid = addGroupSizeError(errors, "maxGroupSize", payload.maxGroupSize);
  if (
    minValid
    && maxValid
    && payload.minGroupSize !== undefined
    && payload.maxGroupSize !== undefined
    && payload.minGroupSize > payload.maxGroupSize
  ) {
    errors.groupSize = "最小团体人数不能大于最大团体人数";
  }
  if (typeof payload.isDisabled !== "boolean") {
    errors.isDisabled = "停用状态不正确";
  }
  if (!resourcePointStatuses.includes(payload.status)) {
    errors.status = "SKU 状态不正确";
  }
  if (codePointLength(payload.remark) > 1000) {
    errors.remark = "备注不能超过 1000 个字符";
  }
  return errors;
}

/** 校验考察点媒体新增或编辑值。 */
export function validateMediaMutation(
  payload: ResourcePointMediaMutation,
): ResourcePointMediaMutationErrors {
  const errors: ResourcePointMediaMutationErrors = {};
  if (!mediaSceneValues.has(payload.mediaScene)) {
    errors.mediaScene = "素材类型不正确";
  }
  const mediaName = payload.mediaName.trim();
  if (!mediaName) {
    errors.mediaName = "素材名称为必填";
  } else if (codePointLength(mediaName) > 300) {
    errors.mediaName = "素材名称不能超过 300 个字符";
  }
  const fileUrl = payload.fileUrl.trim();
  const textOnlyScenes = payload.mediaScene === "description" || payload.mediaScene === "introduction";
  if (!textOnlyScenes && !fileUrl) {
    errors.fileUrl = "请先上传素材文件";
  } else if (codePointLength(fileUrl) > 1000) {
    errors.fileUrl = "文件地址不能超过 1000 个字符";
  }
  if (payload.mediaScene === "introduction") {
    if (!(payload.contentText ?? "").trim()) {
      errors.contentText = "请填写传承人文案";
    }
  }
  if (payload.mediaScene === "ppt") {
    const ext = (payload.fileExt ?? "").toLowerCase().replace(/^\./, "");
    if (ext !== "ppt" && ext !== "pptx") {
      errors.fileUrl = "PPT 仅支持 .ppt 或 .pptx 文件";
    }
  }
  if (payload.mediaScene === "description") {
    const description = parseHeritageDescription(payload.contentText);
    if (
      !description.brief.trim()
      && !description.visitFlow.trim()
      && !description.experienceFlow.trim()
      && !description.priceOther.trim()
    ) {
      errors.contentText = "请至少填写一段描述文案";
    }
  }
  if (!Number.isInteger(payload.sortNo) || payload.sortNo < 0) {
    errors.sortNo = "排序号必须为非负整数";
  }
  if (codePointLength(payload.remark) > 1000) {
    errors.remark = "备注不能超过 1000 个字符";
  }
  return errors;
}

/** 解析描述场景 JSON 文案；非法内容回退为空四段。 */
export function parseHeritageDescription(
  contentText: string | undefined | null,
): ResourcePointHeritageDescription {
  if (!contentText?.trim()) {
    return { ...emptyHeritageDescription };
  }
  try {
    const parsed = JSON.parse(contentText) as Partial<ResourcePointHeritageDescription>;
    return {
      brief: typeof parsed.brief === "string" ? parsed.brief : "",
      visitFlow: typeof parsed.visitFlow === "string" ? parsed.visitFlow : "",
      experienceFlow: typeof parsed.experienceFlow === "string" ? parsed.experienceFlow : "",
      priceOther: typeof parsed.priceOther === "string" ? parsed.priceOther : "",
    };
  } catch {
    return { ...emptyHeritageDescription, brief: contentText };
  }
}

/** 将四段描述序列化为媒体 contentText。 */
export function serializeHeritageDescription(
  description: ResourcePointHeritageDescription,
): string {
  return JSON.stringify({
    brief: description.brief.trim(),
    visitFlow: description.visitFlow.trim(),
    experienceFlow: description.experienceFlow.trim(),
    priceOther: description.priceOther.trim(),
  });
}

/** 按场景分组媒体，供详情页分区展示。 */
export function groupHeritageMediaByScene(
  items: ResourcePointMediaItem[],
): Record<ResourcePointMediaScene, ResourcePointMediaItem[]> {
  const grouped = {
    teaching: [],
    product: [],
    introduction: [],
    ppt: [],
    description: [],
  } as Record<ResourcePointMediaScene, ResourcePointMediaItem[]>;
  for (const item of items) {
    if (mediaSceneValues.has(item.mediaScene as ResourcePointMediaScene)) {
      grouped[item.mediaScene as ResourcePointMediaScene].push(item);
    }
  }
  return grouped;
}

/** 为七牛等 CDN 追加 attname，便于浏览器触发下载。 */
export function buildHeritagePptDownloadUrl(fileUrl: string, mediaName: string, fileExt?: string): string {
  const trimmed = fileUrl.trim();
  const ext = (fileExt ?? "").replace(/^\./, "").toLowerCase() || "pptx";
  const baseName = mediaName.trim() || "PPT";
  const fileName = /\.(ppt|pptx)$/i.test(baseName) ? baseName : `${baseName}.${ext}`;
  try {
    const url = new URL(trimmed);
    url.searchParams.set("attname", fileName);
    return url.toString();
  } catch {
    const joiner = trimmed.includes("?") ? "&" : "?";
    return `${trimmed}${joiner}attname=${encodeURIComponent(fileName)}`;
  }
}

/** 预览地址：优先 HTTPS；当前存储不支持页内嵌入预览，仅供新窗口打开。 */
export function buildHeritagePptOpenUrl(fileUrl: string): string {
  const trimmed = fileUrl.trim();
  try {
    const url = new URL(trimmed);
    if (url.protocol === "http:" && /(^|\.)yishulvxing\.com$/i.test(url.hostname)) {
      url.protocol = "https:";
    }
    return url.toString();
  } catch {
    return trimmed;
  }
}

/** 归一化列表筛选、分页与排序，产出仓储层可直接使用的参数。 */
export function normalizeListFilters(
  filters: ResourcePointListFilters,
  scope: ResourcePointListScope = "all",
): ResourcePointListParams {
  const locked = scopeLockedListFilters(scope);
  let pointType = normalizeOptionalText(filters.pointType);
  let pointSubType = normalizeOptionalText(filters.pointSubType);
  if (locked.pointType) {
    pointType = locked.pointType;
    pointSubType = locked.pointSubType;
  }
  return {
    page: normalizePage(filters.page),
    pageSize: normalizePageSize(filters.pageSize),
    keyword: normalizeOptionalText(filters.keyword),
    pointType,
    pointSubType,
    country: normalizeOptionalText(filters.country),
    province: normalizeOptionalText(filters.province),
    city: normalizeOptionalText(filters.city),
    district: normalizeOptionalText(filters.district),
    status: resourcePointStatuses.includes(filters.status as never) ? filters.status : undefined,
    geoStatus: filters.geoStatus === "confirmed" || filters.geoStatus === "unverified"
      ? filters.geoStatus
      : undefined,
    isDisabled: typeof filters.isDisabled === "boolean" ? filters.isDisabled : undefined,
    sortField: resourcePointSortFields.includes(filters.sortField as never)
      ? filters.sortField as ResourcePointListParams["sortField"]
      : resourcePointDefaultListParams.sortField,
    sortDirection: filters.sortDirection === "asc" || filters.sortDirection === "desc"
      ? filters.sortDirection
      : resourcePointDefaultListParams.sortDirection,
  };
}

/** 根据统一 HTTP 错误码返回稳定的考察点写操作提示。 */
export function resourcePointMutationErrorMessage(error: unknown): string {
  if (!isHttpClientError(error)) {
    return "考察点操作失败，请稍后重试。";
  }
  switch (error.code) {
    case "RESOURCE_POINT_NOT_FOUND":
      return "考察点不存在或已被删除。";
    case "RESOURCE_POINT_MEDIA_NOT_FOUND":
      return "考察点素材不存在或已被删除。";
    case "RESOURCE_POINT_SKU_NOT_FOUND":
      return "考察点 SKU 不存在或已被删除。";
    case "RESOURCE_POINT_UNSUPPORTED_VALUE":
      return "既有未知类型只能原样保留或改为受支持的选项。";
    case "RESOURCE_POINT_CONFLICT":
    case "RESOURCE_POINT_DUPLICATE":
    case "RESOURCE_POINT_SKU_DUPLICATE":
      return error.message?.trim() && !isGenericHttpFailureMessage(error.message)
        ? error.message
        : "考察点或 SKU 的唯一信息冲突，请检查后重试。";
    case "RESOURCE_POINT_DATABASE_UNAVAILABLE":
    case "RESOURCE_POINT_UNAVAILABLE":
      return "考察点数据服务暂时不可用，请稍后重试。";
    case "RESOURCE_POINT_TELECODE_INVALID":
      return error.message?.trim() && !isGenericHttpFailureMessage(error.message)
        ? error.message
        : "未找到对应的有效 12306 电报码，不能新增或编辑，请先核对站名及编码资料。";
    case "RESOURCE_POINT_SHORT_NAME_POLISH_INVALID":
      return error.message?.trim() && !isGenericHttpFailureMessage(error.message)
        ? error.message
        : "简称润色请求无效，请检查名称后重试。";
    case "RESOURCE_POINT_AI_UNAVAILABLE":
      return error.message?.trim() && !isGenericHttpFailureMessage(error.message)
        ? error.message
        : "AI 润色服务暂时不可用，请稍后重试。";
    case "SEMANTIC_DUPLICATE_CHECK_FAILED":
      return error.message?.trim() && !isGenericHttpFailureMessage(error.message)
        ? error.message
        : "名称语义判重失败，请稍后重试。";
    case "MEDIA_CONFIG_MISSING":
      return "媒体上传失败：媒体服务配置缺失，请检查存储配置后重试。";
    case "MEDIA_PROVIDER_ERROR":
      return "媒体上传失败：存储服务暂不可用，请稍后重试。";
    case "MEDIA_VALIDATION_ERROR":
      return error.message.includes("media-too-large")
        ? "媒体上传失败：文件过大，请压缩到 50 MB 以内后重试。"
        : "媒体上传失败：文件校验未通过，请确认格式和大小后重试。";
    case "PAYLOAD_TOO_LARGE":
      return "媒体上传失败：文件过大，请压缩到 50 MB 以内后重试。";
    default:
      return "考察点操作失败，请稍后重试。";
  }
}

/** 检查中国省市区是否符合共享行政区划事实源。 */
function isValidChinaRegion(payload: ResourcePointSpuMutation): boolean {
  const province = normalizeOptionalText(payload.province);
  const city = normalizeOptionalText(payload.city);
  const district = normalizeOptionalText(payload.district);
  if (!province && !city && !district) {
    return true;
  }
  if (!province || !city || !getProvinceOptions().includes(province)) {
    return false;
  }
  if (!getCityOptions(province).includes(city)) {
    return false;
  }
  return !district || getDistrictOptions(province, city).includes(district);
}

/** 添加可选文本的长度错误。 */
function addOptionalLengthError(
  errors: ResourcePointSpuMutationErrors,
  field: ResourcePointSpuMutationField,
  value: string | undefined,
  maxLength: number,
  message: string,
): void {
  if (value !== undefined && codePointLength(value) > maxLength) {
    errors[field] = message;
  }
}

/** 按 Unicode code point 统计去除首尾空白后的文本长度。 */
function codePointLength(value: string): number {
  return Array.from(value.trim()).length;
}

/** 添加经纬度范围错误。 */
function addCoordinateError(
  errors: ResourcePointSpuMutationErrors,
  field: "lng" | "lat",
  value: number | undefined,
  min: number,
  max: number,
  message: string,
): void {
  if (value !== undefined && (!Number.isFinite(value) || value < min || value > max)) {
    errors[field] = message;
  }
}

/** 添加团体人数非负整数错误，并返回当前值是否有效。 */
function addGroupSizeError(
  errors: ResourcePointSkuMutationErrors,
  field: "minGroupSize" | "maxGroupSize",
  value: number | undefined,
): boolean {
  if (value === undefined) {
    return true;
  }
  if (!Number.isInteger(value) || value < 0) {
    errors[field] = field === "minGroupSize"
      ? "最小团体人数必须为非负整数"
      : "最大团体人数必须为非负整数";
    return false;
  }
  return true;
}

/** 校验 SKU 必填文本，同时允许原样保留数据库中的既有未知枚举值。 */
function addRequiredSkuTextError(
  errors: ResourcePointSkuMutationErrors,
  field: "ticketResourceType" | "travelerRole" | "settlementUnit",
  value: string,
  maxLength: number,
  requiredMessage: string,
  lengthMessage: string,
): void {
  const normalized = value.trim();
  if (!normalized) {
    errors[field] = requiredMessage;
  } else if (codePointLength(normalized) > maxLength) {
    errors[field] = lengthMessage;
  }
}

/** 将可选文本去除首尾空白并把空值归一为 undefined。 */
function normalizeOptionalText(value: string | undefined): string | undefined {
  const normalized = value?.trim();
  return normalized ? normalized : undefined;
}

/** 将页码归一到大于等于 1 的整数。 */
function normalizePage(value: number | undefined): number {
  if (value === undefined || !Number.isFinite(value)) {
    return resourcePointDefaultListParams.page;
  }
  return Math.max(1, Math.floor(value));
}

/** 将每页数量归一到 1 至 100。 */
function normalizePageSize(value: number | undefined): number {
  if (value === undefined || !Number.isFinite(value)) {
    return resourcePointDefaultListParams.pageSize;
  }
  return Math.min(100, Math.max(1, Math.floor(value)));
}

/** 仅业务页 GEO 确认并保存后视为已识别。 */
export function hasSavedResourcePointGeo(
  spu: Pick<ResourcePointSpuItem, "geoStatus" | "lng" | "lat">,
): boolean {
  return spu.geoStatus === "confirmed" && spu.lng != null && spu.lat != null;
}

/** 列表已识别记录在地区列换行展示的经纬度文本。 */
export function formatResourcePointListCoordinates(
  spu: Pick<ResourcePointSpuItem, "geoStatus" | "lng" | "lat">,
): string | undefined {
  if (!hasSavedResourcePointGeo(spu) || spu.lng == null || spu.lat == null) {
    return undefined;
  }
  return `${spu.lng.toFixed(6)}, ${spu.lat.toFixed(6)}`;
}

/** 按本次抽屉 GEO 面板状态解析应写入的持久化 GEO 状态。 */
export function resolvedResourcePointGeoStatus(
  payload: Pick<ResourcePointSpuMutation, "lng" | "lat">,
  geoStatus: GeoOperationStatus,
  geoAdopted = false,
): ResourcePointGeoStatus {
  const confirmedByPanel = geoStatus === "confirmed" || geoAdopted;
  return confirmedByPanel && payload.lng != null && payload.lat != null
    ? "confirmed"
    : "unverified";
}

/** 按业务点类型选择统一 GEO 场景。 */
export function resourcePointGeoScenario(pointType: string): GeoBusinessScenario {
  const normalized = pointType.trim();
  if (normalized === "火车站" || normalized === "车站") return "train_station";
  if (normalized === "机场") return "airport";
  return "resource_point";
}

/** 用已保存地址和坐标还原抽屉中的已确认 GEO 面板。 */
export function savedResourcePointGeoResult(
  spu: ResourcePointSpuItem,
): GeoBusinessVerifyResult | undefined {
  if (!hasSavedResourcePointGeo(spu)) return undefined;
  return {
    scenario: resourcePointGeoScenario(spu.pointType),
    status: "confirmed",
    conclusion: "已保存高德标准地址与坐标",
    llmConclusion: "已保存高德标准地址与坐标",
    amapStandardAddress: spu.address ?? "",
    province: spu.province ?? "",
    city: spu.city ?? "",
    district: spu.district ?? "",
    longitude: spu.lng,
    latitude: spu.lat,
    coordinateSystem: "GCJ-02",
    confidenceScore: 0,
    correctionReason: "",
    retryable: false,
    candidates: [],
  };
}
